import axios from 'axios';

// Import the formatCurrencyCode function from src directory
import { formatCurrencyCode } from '../currencyUtils';
import cloudinaryService from './CloudinaryService';
import { GitHubService } from './GitHubService';  // Import the renamed class with static methods

class TokenUploadService {
  constructor() {
    // Use GitHub for TOML hosting and Cloudinary for icons
    this.serverBaseUrl = 'https://raw.githubusercontent.com';
    this.githubOwner = process.env.REACT_APP_GITHUB_OWNER || 'your-github-username';
    this.githubRepo = process.env.REACT_APP_GITHUB_REPO || 'your-repo-name';
    this.githubBranch = 'main';
    this.apiBaseUrl = `/api`; // Use relative API paths
  }

  /**
   * Upload a token icon to Cloudinary
   * @param {File} iconFile - The icon file to upload
   * @param {string} issuer - Issuer address
   * @param {string} currency - Currency code
   * @returns {Promise<string>} - URL to the uploaded icon
   */
  async uploadTokenIcon(iconFile, issuer, currency) {
    try {
      // Format currency code properly
      const formattedCurrency = currency.length <= 3 ? currency : formatCurrencyCode(currency);
      
      // Use CloudinaryService to upload the icon
      return await cloudinaryService.uploadTokenIcon(iconFile, issuer, formattedCurrency);
    } catch (error) {
      console.error('Error uploading token icon:', error);
      throw error;
    }
  }
  
  /**
   * Generate a TOML file for the token and upload it to GitHub
   * @param {Object} tokenData - Token metadata
   * @returns {Promise<string>} - URL to the generated TOML file
   */
  async generateTomlFile(tokenData) {
    try {
      // Format symbol/currency code properly
      const formattedSymbol = tokenData.symbol.length <= 3 
        ? tokenData.symbol 
        : formatCurrencyCode(tokenData.symbol);
      
      // Make sure we have the full icon URL for TOML
      let iconUrl = tokenData.iconUrl || tokenData.icon;
      
      // If we have cloudinaryUrl, use that
      if (tokenData.cloudinaryUrl) {
        iconUrl = tokenData.cloudinaryUrl;
      }
      
      const updatedTokenData = {
        ...tokenData,
        currency: formattedSymbol,  // Ensure we use the right field name
        formattedSymbol,            // Keep for backward compatibility
        icon: iconUrl               // Ensure icon URL is passed correctly
      };
      
      // Use GitHubService static method to generate and upload the TOML file
      const result = await GitHubService.generateTomlFile(updatedTokenData);
      
      // Handle the new response format from GitHubService
      if (result) {
        // If success is false but we have a fallback message, continue with local TOML only
        if (result.success === false) {
          console.warn('TOML hosting service returned an error, using local TOML only:', result.message);
          // Return a compatible result format
          return {
            tomlUrl: null,
            hostedUrl: null,
            localOnly: true,
            message: result.message || 'Using local TOML file only due to service unavailability'
          };
        }
        
        // Return the success result (either tomlUrl from old format or hostedUrl from new format)
        return {
          tomlUrl: result.tomlUrl || result.hostedUrl,
          hostedUrl: result.hostedUrl || result.tomlUrl,
          localOnly: false
        };
      }
      
      // Fallback for unexpected response
      return {
        tomlUrl: null,
        hostedUrl: null,
        localOnly: true,
        message: 'TOML hosting service returned an unexpected response format. Using local TOML file only.'
      };
    } catch (error) {
      console.error('Error generating TOML file:', error);
      
      // Return a compatible format instead of throwing, so token creation can continue
      return {
        tomlUrl: null,
        hostedUrl: null,
        localOnly: true,
        error: error.message,
        message: 'Failed to generate TOML file on server. Using local TOML file only.'
      };
    }
  }
  
  /**
   * Get the full TOML content for instructions on self-hosting
   * @param {Object} tokenData - The token data to include in the TOML
   * @returns {Promise<string>} - TOML file content
   */
  async getTomlContent(tokenData) {
    try {
      // Ensure icon URL is passed correctly
      const updatedTokenData = {
        ...tokenData,
        icon: tokenData.iconUrl || tokenData.icon || tokenData.cloudinaryUrl
      };
      
      // Generate TOML content locally since the GitHub service might be unavailable
      // This is a simplified fallback implementation
      const tomlContent = `
[CURRENCIES.${updatedTokenData.symbol}]
issuer = "${updatedTokenData.issuer}"
code = "${updatedTokenData.symbol}"
display_decimals = 6
name = "${updatedTokenData.name}"
desc = "${updatedTokenData.description || ''}"
${updatedTokenData.icon ? `image = "${updatedTokenData.icon}"` : ''}
${updatedTokenData.website ? `website = "${updatedTokenData.website}"` : ''}
${updatedTokenData.twitter ? `twitter = "${updatedTokenData.twitter}"` : ''}
${updatedTokenData.telegram ? `telegram = "${updatedTokenData.telegram}"` : ''}
`;
      
      return tomlContent.trim();
    } catch (error) {
      console.error('Error getting TOML content:', error);
      // Return a minimal valid TOML rather than throwing
      return `
[CURRENCIES.${tokenData.symbol}]
issuer = "${tokenData.issuer}"
code = "${tokenData.symbol}"
`;
    }
  }
  
  /**
   * Register a domain for proxy TOML hosting
   * @param {string} domain - The domain to register
   * @param {Object} tokenData - Token data for the TOML file
   * @returns {Promise<Object>} - Registration details
   */
  async registerDomainForHosting(domain, tokenData) {
    try {
      // Make sure the domain is formatted correctly
      const cleanDomain = domain.trim().toLowerCase();
      if (cleanDomain.startsWith('http://') || cleanDomain.startsWith('https://')) {
        const url = new URL(cleanDomain);
        domain = url.hostname;
      }
      
      const response = await axios.post(`${this.apiBaseUrl}/register-domain`, {
        domain,
        tokenData
      });
      
      return response.data;
    } catch (error) {
      console.error('Error registering domain for TOML hosting:', error);
      throw error;
    }
  }
  
  /**
   * Check if a domain has DNS TXT records set up for TOML discovery
   * @param {string} domain - Domain to check
   * @returns {Promise<Object>} - DNS TXT record information
   */
  async checkDnsTxtRecords(domain) {
    try {
      const response = await axios.get(`/api/dns-txt/${domain}`);
      return response.data;
    } catch (error) {
      console.error('Error checking DNS TXT records:', error);
      return { error: true, message: error.message };
    }
  }
  
  /**
   * Generate MD5 hash for token identification
   * @param {string} issuer - Issuer address
   * @param {string} currency - Currency code
   * @returns {string} - MD5 hash
   */
  generateMd5(issuer, currency) {
    // Simple hash function for client-side
    let str = issuer + '_' + currency;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }
  
  /**
   * Export all tokens created by an issuer for self-hosting
   * @param {string} issuer - Issuer address
   * @returns {Promise<Object>} - Export data with combined TOML
   */
  async exportTokensForSelfHosting(issuer) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/export`, {
        params: { issuer }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error exporting tokens for self-hosting:', error);
      throw error;
    }
  }
  
  /**
   * Get instructions for users on how to host their own TOML file
   * @param {Object} tokenData - Token data
   * @returns {Object} - Instructions and TOML content
   */
  async getTomlHostingInstructions(tokenData) {
    try {
      const tomlContent = await this.getTomlContent(tokenData);
      
      // Calculate URLs for GitHub-hosted TOML
      const currencyCode = tokenData.symbol || tokenData.currency || '';
      const githubTomlUrl = `${this.serverBaseUrl}/${this.githubOwner}/${this.githubRepo}/${this.githubBranch}/.well-known/xrp-ledger.toml#${currencyCode.toLowerCase()}`;
      
      return {
        tomlContent,
        serverTomlUrl: githubTomlUrl,
        proxyUrl: githubTomlUrl,
        iconInstructions: [
          `We've automatically stored your token icon on Cloudinary.`,
          `This URL is already referenced in your TOML file.`
        ],
        message: 'Your token TOML file is automatically hosted on GitHub. No additional configuration is needed.',
        hostedOn: 'GitHub'
      };
    } catch (error) {
      console.error('Error getting TOML hosting instructions:', error);
      throw error;
    }
  }
}

const tokenUploadService = new TokenUploadService();
export default tokenUploadService; 